import { useState } from "react";
import { Link } from "react-router-dom";
import { Siren, Phone, Navigation, Loader2, MapPin } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { api } from "../lib/api";

const GEO_ERR = "Vi kunne ikke bruge din placering. Skriv dit postnummer eller din by.";

const digits = (s) => (s || "").replace(/\D/g, "").slice(-8);
const fmt = (s) => { const d = digits(s); return d.length === 8 ? d.replace(/(\d{2})(?=\d)/g, "$1 ").trim() : s; };
const CallLink = ({ num, label, primary, testid }) => (
  <a href={`tel:+45${digits(num)}`} className={primary ? "btn-primary btn-sm" : "btn-outline btn-sm"} data-testid={testid}><Phone className="h-4 w-4" />{label} · {fmt(num)}</a>
);

const Place = ({ p, i, kind }) => {
  const vagt = !p.is_24_7 && p.on_call_phone;
  const showMain = p.phone && (!vagt || digits(p.phone) !== digits(p.on_call_phone));
  return (
  <li className="card-vp p-4" data-testid={`emergency-${kind}-${i}`}>
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <Link to={`/dyreklinikker/${p.slug}`} className="font-bold text-[#153C3A] hover:underline" data-testid={`emergency-${kind}-name-${i}`}>{p.name}</Link>
        <p className="text-sm text-[#52706D] flex items-center gap-1 mt-0.5"><MapPin className="h-3.5 w-3.5 shrink-0" />{[p.address, [p.postal_code, p.city].filter(Boolean).join(" ")].filter(Boolean).join(", ") || "Adresse ikke oplyst"}</p>
      </div>
      <span className="text-sm font-semibold text-[#087F72] whitespace-nowrap" data-testid={`emergency-${kind}-distance-${i}`}>{p.distance_km.toLocaleString("da-DK")} km</span>
    </div>
    {p.is_24_7 && p.source_url && <p className="text-[11px] text-[#52706D] mt-1">Døgnåben ifølge <a href={p.source_url} target="_blank" rel="noopener noreferrer nofollow" className="underline">{p.source_url.includes("openstreetmap") ? "OpenStreetMap" : "klinikkens hjemmeside"}</a></p>}
    {vagt && <p className="text-[11px] text-[#52706D] mt-1" data-testid={`emergency-${kind}-oncall-note-${i}`}>Vagttelefon ifølge <a href={p.on_call_source} target="_blank" rel="noopener noreferrer nofollow" className="underline">klinikkens hjemmeside</a></p>}
    <div className="flex flex-wrap gap-2 mt-3">
      {vagt && <CallLink num={p.on_call_phone} label="Ring vagt" primary testid={`emergency-${kind}-oncall-${i}`} />}
      {showMain && <CallLink num={p.phone} label="Ring nu" primary={!vagt} testid={`emergency-${kind}-call-${i}`} />}
      {!showMain && !vagt && <span className="text-xs text-[#52706D] self-center" data-testid={`emergency-${kind}-no-phone-${i}`}>Telefonnummer ikke oplyst – se klinikkens side</span>}
      <a href={`https://www.google.com/maps/dir/?api=1&destination=${p.lat},${p.lng}`} target="_blank" rel="noopener noreferrer" className="btn-outline btn-sm" data-testid={`emergency-${kind}-directions-${i}`}><Navigation className="h-4 w-4" />Vis vej</a>
    </div>
  </li>
  );
};

const OpenList = ({ data }) => (
  data.open_24_7.length > 0 ? (
    <div>
      <h3 className="text-sm font-bold text-[#153C3A] mb-2">Døgnåbne dyrehospitaler</h3>
      <ul className="space-y-3">{data.open_24_7.map((p, i) => <Place key={p.clinic_id} p={p} i={i} kind="open" />)}</ul>
    </div>
  ) : <p className="text-sm text-[#153C3A]" data-testid="emergency-none-open">Vi kender ikke et døgnåbent dyrehospital i nærheden. Herunder er de nærmeste dyrehospitaler og vagttelefoner – ring først.</p>
);

const LocalList = ({ data }) => data.hospitals.length > 0 && (
  <div>
    <h3 className="text-sm font-bold text-[#153C3A] mb-2">Nærmeste dyrehospitaler og vagttelefoner – ring først</h3>
    <ul className="space-y-3">{data.hospitals.map((p, i) => <Place key={p.clinic_id} p={p} i={i} kind="hospital" />)}</ul>
  </div>
);

export const Results = ({ data }) => {
  const far = data.open_24_7.length > 0 && data.open_24_7[0].distance_km > 40 && data.hospitals.length > 0;
  return (
    <div className="space-y-4" data-testid="emergency-results">
      {far && <p className="text-sm text-[#153C3A]" data-testid="emergency-far-note">Det nærmeste døgnåbne dyrehospital ligger {data.open_24_7[0].distance_km.toLocaleString("da-DK")} km væk. Ring først til en lokal klinik eller vagttelefon.</p>}
      {far ? <><LocalList data={data} /><OpenList data={data} /></> : <><OpenList data={data} /><LocalList data={data} /></>}
      <p className="text-xs text-[#52706D]" data-testid="emergency-disclaimer">Ring altid, før du kører – åbningstider og vagtordninger kan ændre sig. Afstande er i luftlinje.</p>
    </div>
  );
};

export const EmergencyButton = ({ className = "", testid = "emergency-button" }) => {
  const [open, setOpen] = useState(false);
  const [state, setState] = useState({ busy: false, data: null, error: "" });
  const [text, setText] = useState("");
  const lookup = async (location) => {
    setState({ busy: true, data: null, error: "" });
    try {
      const r = await api.get("/public/emergency/nearest", { params: { location } });
      setState({ busy: false, data: r.data, error: "" });
    } catch (e) {
      setState({ busy: false, data: null, error: e.response?.data?.detail || "Noget gik galt. Prøv igen." });
    }
  };
  const start = () => {
    setOpen(true);
    if (!navigator.geolocation) { setState({ busy: false, data: null, error: GEO_ERR }); return; }
    setState({ busy: true, data: null, error: "" });
    navigator.geolocation.getCurrentPosition(
      (pos) => lookup(`${pos.coords.latitude.toFixed(3)},${pos.coords.longitude.toFixed(3)}`),
      () => setState({ busy: false, data: null, error: GEO_ERR }),
      { timeout: 10000, maximumAge: 300000 },
    );
  };
  return (
    <>
      <button type="button" onClick={start} className={`inline-flex items-center justify-center gap-2 rounded-full bg-[#B42318] hover:bg-[#912018] text-white font-semibold px-5 py-2.5 text-sm transition-colors ${className}`} data-testid={testid}>
        <Siren className="h-4 w-4" />Nærmeste døgnåbne dyrehospital
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="emergency-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#153C3A]"><Siren className="h-5 w-5 text-[#B42318]" />Nærmeste døgnåbne dyrehospital</DialogTitle>
            <DialogDescription>{state.data ? `Ud fra: ${state.data.place.label}` : "Vi finder det nærmeste sted ud fra din placering."}</DialogDescription>
          </DialogHeader>
          {state.busy && <p className="flex items-center gap-2 text-sm text-[#52706D]" data-testid="emergency-loading"><Loader2 className="h-4 w-4 animate-spin" />Finder nærmeste sted…</p>}
          {state.error && (
            <form onSubmit={(e) => { e.preventDefault(); if (text.trim()) lookup(text.trim()); }} className="space-y-2" data-testid="emergency-manual-form">
              <p className="text-sm text-[#B42318]" data-testid="emergency-error">{state.error}</p>
              <div className="flex gap-2">
                <input className="field-vp flex-1" value={text} onChange={(e) => setText(e.target.value)} placeholder="Postnummer eller by" aria-label="Postnummer eller by" data-testid="emergency-location-input" />
                <button className="btn-primary btn-sm" type="submit" data-testid="emergency-location-submit">Find</button>
              </div>
            </form>
          )}
          {state.data && <Results data={state.data} />}
        </DialogContent>
      </Dialog>
    </>
  );
};
